/* ══ F15 STEP 3.4 / 3.7 — HIS LADDER RULE, THE SERVER'S TWIN OF `CoachLadder.swift` ══════════════════════════════════════
 * Reach a target on this exercise, then the next one. Stored as one more rule kind in the `{"rule": …}` vocabulary:
 *     {"rule": "ladder", "links": [{"next": "<coach_exercises.id>", "reps": 10, "kg": 20, "seconds": 30, "sessions": 1}, …]}
 * Link i is "when THIS exercise reaches the target, the row becomes `next`". The POSITION is derived, never stored
 * (`workoutRules.effectiveRule` hands a moved row the rest of the chain that names it).
 *
 * 🔴 THE SWIFT BINDS, SENTENCE FOR SENTENCE. Pinned to it by `progression-fixture.json` (the `f15/ladder-*` cases: verdict
 * `ladderSaid`/`moves` and each case's `writer`). `web/src/lib/workoutEngine.ts` carries the web's partial copy for the
 * editor; `_shared` may not import from `web/`, so the reading is stated again here and the two are run over one corpus
 * (`workoutRuleCorpus.json`) by `sharedWorkoutRules.test.ts`.
 * ⚠️ JSON READS AS THE SWIFT'S `JSONValue` READS: a number is a number (a string "10" is not a target); `intValue` is a
 * number with no fraction (10.5 is not 10); `kg` is any number above zero.
 * ⚠️ STRICTER THAN `performance` IN ONE PLACE, ON PURPOSE: a completed set that logged NO number is NOT reached.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

import { roundHalfAwayFromZero } from "./progression.ts";

export const LADDER_RULE = "ladder";

/** `CoachLadder.Link` — `next` lower-cased; targets absent = null; `sessions` 1 or 2 (consecutive logged sessions). */
export interface LadderLink {
  next: string;
  reps: number | null;
  kg: number | null;
  seconds: number | null;
  sessions: 1 | 2;
}

/** `CoachLadder.Chain` — the readable links in order; `brokenAt` = the first unreadable one (the chain ENDS there). */
export interface LadderChain {
  links: LadderLink[];
  brokenAt: number | null;
}

/** `CoachLadder.Target` — `coach_exercises` read by id; absent = deleted. `repsMin`/`holdSecondsMin` are its own first
 *  prescription (`typical_rep_range_min` / `typical_hold_seconds_min`); null = the row keeps its number. */
export interface LadderTarget {
  id: string;
  name: string;
  isHold: boolean;
  repsMin: number | null;
  holdSecondsMin: number | null;
}

/** `CoachLadder.Logged` — one logged working set as the ladder reads it. */
export interface LadderLogged {
  reps: number | null;
  seconds: number | null;
  kg: number | null;
  status: string;
}

const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
/** `JSONValue.intValue`: a number equal to its own rounding. */
const intValue = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) ? v : null);
/** `JSONValue.doubleValue`: any number. */
const doubleValue = (v: unknown): number | null => (typeof v === "number" ? v : null);

/** `CoachLadder.isLadder`. */
export function isLadder(config: unknown): boolean {
  return isObj(config) && config["rule"] === LADDER_RULE;
}

/** `CoachLadder.link` — a key that is PRESENT must read; a wrong value is never "no target". */
function readLink(item: unknown): LadderLink | null {
  if (!isObj(item)) return null;
  const rawNext = item["next"];
  if (typeof rawNext !== "string") return null;
  const next = rawNext.toLowerCase();
  if (next === "") return null;
  const present = (key: string): boolean => item[key] !== undefined && item[key] !== null;
  const reps = intValue(item["reps"]), kg = doubleValue(item["kg"]), seconds = intValue(item["seconds"]);
  if (present("reps") && (reps ?? 0) < 1) return null;
  if (present("kg") && (kg ?? 0) <= 0) return null;
  if (present("seconds") && (seconds ?? 0) < 1) return null;
  if (reps === null && kg === null && seconds === null) return null;
  let sessions: 1 | 2;
  if (present("sessions")) {
    const s = intValue(item["sessions"]);
    if (s !== 1 && s !== 2) return null;
    sessions = s;
  } else sessions = 1;
  return { next, reps, kg, seconds, sessions };
}

/** `CoachLadder.chain` — null when the config is not a ladder; `links` not an array = broken at 0. */
export function ladderChain(config: unknown): LadderChain | null {
  if (!isLadder(config)) return null;
  const raw = (config as Record<string, unknown>)["links"];
  if (!Array.isArray(raw)) return { links: [], brokenAt: 0 };
  const links: LadderLink[] = [];
  for (let index = 0; index < raw.length; index++) {
    const link = readLink(raw[index]);
    if (link === null) return { links, brokenAt: index };
    links.push(link);
  }
  return { links, brokenAt: null };
}

/** `CoachLadder.json` — the links as JSON again (what a derived position hands `Row.rule`). Absent targets stay absent. */
export function ladderJson(links: readonly LadderLink[]): { rule: string; links: Record<string, unknown>[] } {
  return {
    rule: LADDER_RULE,
    links: links.map((l) => {
      const o: Record<string, unknown> = { next: l.next, sessions: l.sessions };
      if (l.reps !== null) o["reps"] = l.reps;
      if (l.kg !== null) o["kg"] = l.kg;
      if (l.seconds !== null) o["seconds"] = l.seconds;
      return o;
    }),
  };
}

/** `CoachLadder.rest(of:after:)` — the rest of `config`'s chain after the FIRST link naming `exerciseId`; null when the chain
 *  never reaches it (a link after an unreadable one is never reached). */
export function ladderRest(config: unknown, exerciseId: string): LadderLink[] | null {
  const chain = ladderChain(config);
  if (chain === null) return null;
  const id = exerciseId.toLowerCase();
  const at = chain.links.findIndex((l) => l.next === id);
  if (at < 0) return null;
  return chain.links.slice(at + 1);
}

// ── Reached — measured from the logged sets ─────────────────────────────────────────────────────────────────────────────

const loggedCompleted = (s: LadderLogged): boolean => s.status === "completed" || s.status === "partial";
const loggedWarmup = (s: LadderLogged): boolean => s.status === "warmup";

/** `CoachLadder.reached` — ONE session: every prescribed WORKING set completed, each at or above the target's numbers. */
export function reached(link: LadderLink, prescribedSets: number, logged: LadderLogged[]): boolean {
  const done = logged.filter((s) => !loggedWarmup(s) && loggedCompleted(s));
  if (!(prescribedSets > 0 && done.length >= prescribedSets)) return false;
  return done.every((set) => {
    if (link.reps !== null && (set.reps === null || !(set.reps >= link.reps))) return false;
    if (link.seconds !== null && (set.seconds === null || !(set.seconds >= link.seconds))) return false;
    if (link.kg !== null && (set.kg === null || !(set.kg + 1e-9 >= link.kg))) return false;
    return true;
  });
}

/** `CoachLadder.streak` — consecutive sessions at the target, most recent first; a null session (nothing usable logged) is
 *  SKIPPED, never a miss. */
export function streak(link: LadderLink, prescribedSets: number, sessions: (LadderLogged[] | null)[]): number {
  let count = 0;
  for (const sets of sessions) {
    if (sets === null) continue;
    if (!reached(link, prescribedSets, sets)) return count;
    count += 1;
  }
  return count;
}

/** `CoachLadder.isDue`. */
export function ladderIsDue(link: LadderLink, prescribedSets: number, sessions: (LadderLogged[] | null)[]): boolean {
  return streak(link, prescribedSets, sessions) >= link.sessions;
}

// ── Words (no engine words on screen) ───────────────────────────────────────────────────────────────────────────────────

/** C's `%g` (precision 6), which `String(format: "%g", …)` is: six significant digits, trailing zeros dropped, the exponent
 *  form below 1e-4 or from 1e6. */
export function percentG(v: number): string {
  if (Number.isNaN(v)) return "nan";
  if (!Number.isFinite(v)) return v > 0 ? "inf" : "-inf";
  if (v === 0) return Object.is(v, -0) ? "-0" : "0";
  const strip = (s: string): string => (s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s);
  const [mantissa, exponent] = v.toExponential(5).split("e") as [string, string];
  const x = Number(exponent);
  if (x < -4 || x >= 6) {
    return `${strip(mantissa)}e${x < 0 ? "-" : "+"}${String(Math.abs(x)).padStart(2, "0")}`;
  }
  return strip(v.toFixed(5 - x));
}

/** `CoachLadder.kgText` — a whole number as an Int, else `%g` of the value rounded to the hundredth. */
export function kgText(v: number): string {
  return v === roundHalfAwayFromZero(v) ? String(Math.trunc(v)) : percentG(roundHalfAwayFromZero(v * 100) / 100);
}

/** `CoachLadder.target` — "10 reps", "8 reps @ 20 kg", "30 s", "20 kg" (+ " twice running"). */
export function targetText(link: Pick<LadderLink, "reps" | "kg" | "seconds" | "sessions">): string {
  const amount: string[] = [];
  if (link.reps !== null) amount.push(`${link.reps} reps`);
  if (link.seconds !== null) amount.push(`${link.seconds} s`);
  const load = link.kg !== null ? `${kgText(link.kg)} kg` : null;
  const text = amount.length === 0 ? (load ?? "") : amount.join(", ") + (load !== null ? ` @ ${load}` : "");
  return text + (link.sessions === 2 ? " twice running" : "");
}

/** `CoachLadder.moveSentence` — `dose` is next week's working dose ("3 × 5", "3 × 20 s"); null when the sets name no number. */
export function moveSentence(from: string, link: LadderLink, target: LadderTarget, dose: string | null): string {
  return `${from}: ${targetText(link)} on every set — next week ${target.name}${dose !== null ? `, ${dose}` : ""}.`;
}

/** `CoachLadder.notYetSentence`. */
export function notYetSentence(name: string, link: LadderLink): string {
  return `${name}: stays until every set reaches ${targetText(link)}.`;
}

/** `CoachLadder.endSentence`. */
export function endSentence(name: string): string {
  return `${name}: last step of its ladder — stays as written.`;
}

/** `CoachLadder.deletedSentence`. */
export function deletedSentence(name: string): string {
  return `${name}: its next exercise was deleted — the ladder ends here, stays as written.`;
}

/** `CoachLadder.unreadableSentence`. */
export function unreadableSentence(name: string): string {
  return `${name}: its next step can't be read — the ladder ends here, stays as written.`;
}
